import { useEffect, useState } from "react";
import { ChevronsUpDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api } from "@/lib/api";
import { ItemPickerFilters } from "@/components/app/ItemPickerFilters";

// Server-searched single picker. `url` must accept ?q=; `render` maps an item to {title, subtitle}; `itemFilters` adds group/sub-group narrowing.
export function EntityPicker({ url, value, onChange, render, placeholder, testid, footer, itemFilters }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [flt, setFlt] = useState({ group: "", sub_group: "" });

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const sep = url.includes("?") ? "&" : "?";
      const extra = Object.entries(flt).filter(([, v]) => v).map(([k, v]) => `&${k}=${encodeURIComponent(v)}`).join("");
      api.get(`${url}${sep}q=${encodeURIComponent(q)}&limit=${extra ? 200 : 30}${extra}`).then((r) => setItems(r.data)).catch(() => setItems([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open, url, flt]);

  const sel = value ? render(value) : null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
          className="w-full min-h-9 flex items-center justify-between gap-2 px-3 py-1.5 text-sm border border-input rounded-md bg-white text-left">
          {sel ? (
            <span className="truncate" title={sel.title}><span className="font-medium">{sel.title}</span> <span className="text-slate-500 text-xs">{sel.subtitle}</span></span>
          ) : <span className="text-slate-400">{placeholder}</span>}
          <span className="flex items-center gap-1 shrink-0">
            {value && <X className="h-4 w-4 text-slate-400 hover:text-slate-700" onClick={(e) => { e.stopPropagation(); onChange(null); }} data-testid={`${testid}-clear`} />}
            <ChevronsUpDown className="h-4 w-4 text-slate-400" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className={`p-0 w-[--radix-popover-trigger-width] ${itemFilters ? "min-w-[340px]" : "min-w-[280px]"}`} align="start">
        <Command shouldFilter={false}>
          {itemFilters && <ItemPickerFilters value={flt} onChange={setFlt} testid={testid} />}
          <CommandInput placeholder="Type to search…" value={q} onValueChange={setQ} data-testid={`${testid}-search`} />
          <CommandList>
            {!items.length && <CommandEmpty>No matches</CommandEmpty>}
            {items.map((it) => {
              const r = render(it);
              if (it.hint_only) return (
                <div key={it.id} className="px-2 py-2 text-xs text-amber-700 bg-amber-50 rounded" data-testid={`${testid}-blocked-hint`}>
                  <span className="font-mono font-semibold">{it.item_code}</span> {it.item_name}<div className="font-medium">{it.blocked_message}</div>
                </div>
              );
              return (
                <CommandItem key={it.id} value={it.id} onSelect={() => { onChange(it); setOpen(false); }} data-testid={`${testid}-option-${it.id}`}>
                  <div className="min-w-0"><div className="text-sm font-medium truncate">{r.title}</div><div className="text-xs text-slate-500 truncate">{r.subtitle}</div></div>
                </CommandItem>
              );
            })}
          </CommandList>
          {footer && <div className="border-t border-slate-200 p-2">{footer}</div>}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export const customerRender = (c) => ({ title: c.name, subtitle: `${c.code} · ${c.gstins?.length ? `${c.gstins[0]}${c.gstins.length > 1 ? ` +${c.gstins.length - 1}` : ""}` : "GST pending"}${c.city ? ` · ${c.city}` : ""}` });
export const partRender = (p) => ({ title: p.part_code, subtitle: `${p.description} · ${p.uom || ""}` });
export const itemRender = (p) => ({ title: `${p.item_code} – ${p.item_name}`, subtitle: [p.uom, p.sub_group, p.make_brand, p.hsn_sac ? `HSN ${p.hsn_sac}` : "no HSN", p.gst_percent != null ? `GST ${p.gst_percent}%` : ""].filter(Boolean).join(" · ") });
export const ITEM_URL = "/items?status=ACTIVE&hint=1";
